import { OktaAuth } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { UserApi } from "../api/UserApi";
import config from "../config";
import { User } from "../types/UserTypes";
import { PdlClub } from "../types/ClubTypes";
import { datadogRum } from "@datadog/browser-rum";

type UserInfo = User & {
  accessToken: { accessToken: string };
  oktaId?: string;
};

type UserContext = {
  userInfo: UserInfo;
  loggedIn: boolean;
  login: (data: UserInfo) => void;
  logout: () => void;
  permissionError: boolean;
  loginFunction: SetStateAction<void>;
  setPermissionError: Dispatch<SetStateAction<boolean>>;
  setLoginFunction: Dispatch<SetStateAction<void>>;
  getUserId(): number;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  setActiveClub: (club: PdlClub) => void;
};

const UserContext = createContext<UserContext>({
  userInfo: null,
  loggedIn: false,
  login: null,
  logout: null,
  permissionError: false,
  loginFunction: null,
  setPermissionError: null,
  setLoginFunction: null,
  getUserId: null,
  hasPermission: null,
  hasAnyPermission: null,
  setActiveClub: null,
});

const initialState = {
  userInfo: {
    accessToken: { accessToken: "" },
    userName: "",
    userFullName: "",
    email: "",
    orgId: 0,
    orgName: "",
    orgShortName: "",
    permissions: ["NONE"],
    userId: 0,
  },
  loggedIn: false,
};

const oktaAuth = new OktaAuth(config.oidc);

const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [userState, setUserState] = useState<UserInfo | undefined>();
  const [loggedIn, setLoggedIn] = useState<boolean>(initialState.loggedIn);
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [loginFunction, setLoginFunction] = useState(null);
  const { authState } = useOktaAuth();

  const getUserId = useCallback((): number => {
    return userState.id;
  }, [userState]);

  const login = useCallback(async (userData: UserInfo): Promise<void> => {
    setLoggedIn(true);

    // TODO Fill in with application-specific user info call
    const userInfoData: UserInfo = await UserApi.getUserInfo();
    if (userInfoData) {
      try {
        const datadogUser = {
          id: userInfoData.id.toString(),
          email: userInfoData.username,
          name: userInfoData.firstName + " " + userInfoData.lastName,
          role: userInfoData.role.label,
        };
        datadogRum.setUser(datadogUser);
      } catch (error) {
        console.error(error);
      }
      userData = { ...userData, ...userInfoData };
      setUserInfoData(userData);
    }
    setUserState(userData);
  }, []);

  const setUserInfoData = useCallback((userInfo: UserInfo) => {
    setUserState(userInfo);
    setLoggedIn(true);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await oktaAuth.signOut();
  }, []);

  const setActiveClub = useCallback(
    async (club: PdlClub) => {
      const userInfoData = await UserApi.updateActiveClub(club);
      setUserInfoData(userInfoData);
    },
    [setUserInfoData],
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return !!userState?.permissions.filter((p) => p.name === permission).length;
    },
    [userState?.permissions],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      return !!permissions.filter((permission) => hasPermission(permission)).length;
    },
    [hasPermission],
  );

  useEffect(() => {
    if (authState?.isAuthenticated) {
      // when the user becomes authenticated, call onLogin() to populate AuthContext's user info
      // @ts-ignore
      login({ accessToken: authState.accessToken, oktaId: authState.accessToken?.claims?.uid });
    }
  }, [authState]);

  return (
    <UserContext.Provider
      value={{
        userInfo: userState,
        loggedIn,
        login,
        logout,
        permissionError,
        setPermissionError,
        loginFunction,
        setLoginFunction,
        getUserId,
        hasAnyPermission,
        hasPermission,
        setActiveClub,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUser = (): UserContext => {
  const userContext = useContext<UserContext>(UserContext);
  if (userContext === undefined) {
    throw new Error(`useUser must be used within a UserProvider`);
  }
  return userContext;
};

export { UserContext, UserProvider, oktaAuth, useUser, UserInfo };
